import { useSegmentation } from '@printdeal/segmentation/react';
import { useConsent } from '@printdeal/consent-management/react';
import { type FC, useEffect } from 'react';
import { ErrorManagement } from '@printdeal/error-management/react';

interface MopinionScriptProps {
  deploymentId: string;
  enabled: boolean;
}
declare global {
  interface Window {
    Pastease?: {
      load: (id: string) => void;
    };
    isReseller?: boolean;
  }
}

export const MopinionScript: FC<MopinionScriptProps> = ({ deploymentId, enabled }) => {
  const segmentationData = useSegmentation();
  const isReseller = segmentationData.isReseller();
  const isWindow = typeof window !== 'undefined';

  const scriptId = 'mopinion-script';
  const consent = useConsent();
  const hasMarketingConsent = consent?.marketing;

  useEffect(() => {
    /**
   * Set a global variable `window.isReseller` so that external tools like Mopinion
   * can check if the user is a reseller.
   */
    if (isWindow) {
      window.isReseller = isReseller;
    }
  }, [isReseller, isWindow]);

  useEffect(() => {
    if (!isWindow) {
      return;
    }
    if (enabled && !isReseller && hasMarketingConsent) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.async = true;
      script.src = 'https://deploy.mopinion.com/js/pastease.js';
      script.onload = () => {
        if (window.Pastease) {
          try {
            window.Pastease.load(deploymentId);
          } catch (e) {
            ErrorManagement.exception({
              exception: e,
              tags: {
                fileLocation: 'MopinionScript',
                featureFunction: 'load',
              },
              fingerprint: ['mopinion-script-load'],
              extra: {
                source: 'MopinionScript',
                reason: 'Pastease.load failed',
                deploymentId,
              },
            });
          }
        }
      };
      document.head.appendChild(script);
    }
  }, [isReseller, enabled, hasMarketingConsent, isWindow, deploymentId]);

  return null;
};
